import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from '@mui/material';
import {
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Person,
  Info,
  Tune,
} from '@mui/icons-material';
import { collection, query, orderBy, getDocs, doc, updateDoc, getDoc, where, addDoc, Timestamp, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { PageHeader, ResponsiveTable, SectionCard, StatusChip } from '../components/common';

/** A "label above value" pair — the detail-dialog equivalent of a form field, read-only. */
const Field: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body1" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
      {value || value === 0 ? value : <Box component="span" sx={{ color: 'text.disabled', fontWeight: 400 }}>Not provided</Box>}
    </Typography>
  </Box>
);

interface PreferenceRequest {
  id: string;
  ninjaId: string;
  ninjaName?: string;
  ninjaEmail?: string;
  currentSegments: string[];
  requestedSegments: string[];
  status: 'pending' | 'approved' | 'rejected';
  requestedAt: Date;
  requestedBy: string;
  adminNotes: string;
  processedAt?: Date;
  processedBy?: string;
}

const serviceSegmentLabels: { [key: string]: string } = {
  delivery: 'Delivery Services',
  home_services: 'Home Services',
  personal_care: 'Personal Care',
  professional: 'Professional Services',
  transportation: 'Transportation',
};

const NinjaPreferenceApprovals: React.FC = () => {
  const [requests, setRequests] = useState<PreferenceRequest[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<PreferenceRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState<PreferenceRequest | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [serviceSegmentsMap, setServiceSegmentsMap] = useState<{ [key: string]: string }>({});
  const [serviceCategoriesMap, setServiceCategoriesMap] = useState<{ [key: string]: string }>({});
  
  // Filter states
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [segmentFilter, setSegmentFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  useEffect(() => {
    // Issue #276: Set up real-time listener for preference requests
    let unsubscribe: (() => void) | undefined;

    const initialize = async () => {
      try {
        setLoading(true);

        // Check authentication first
        const currentUser = auth.currentUser;
        if (!currentUser) {
          console.error('❌ [NinjaPreferenceApprovals]: Admin not authenticated');
          showSnackbar('Authentication required', 'error');
          setLoading(false);
          return;
        }

        console.log('✅ [NinjaPreferenceApprovals]: Admin authenticated:', currentUser.email);

        // Fetch service segments and categories
        try {
          const segmentsRef = collection(db, 'service_segments');
          const segmentsQuery = query(segmentsRef, where('isActive', '==', true));
          const segmentsSnapshot = await getDocs(segmentsQuery);
          const segmentsMap: { [key: string]: string } = {};
          segmentsSnapshot.forEach((doc) => {
            const data = doc.data();
            segmentsMap[doc.id] = data.name || doc.id;
          });
          setServiceSegmentsMap(segmentsMap);

          const categoriesRef = collection(db, 'service_categories');
          const categoriesQuery = query(categoriesRef, where('isActive', '==', true));
          const categoriesSnapshot = await getDocs(categoriesQuery);
          const categoriesMap: { [key: string]: string } = {};
          categoriesSnapshot.forEach((doc) => {
            const data = doc.data();
            categoriesMap[doc.id] = data.name || doc.id;
          });
          setServiceCategoriesMap(categoriesMap);

          console.log('✅ Loaded service segments:', segmentsMap);
          console.log('✅ Loaded service categories:', categoriesMap);
        } catch (error) {
          console.error('Error loading service data:', error);
        }

        // Issue #276: Use onSnapshot for real-time updates instead of getDocs
        const requestsRef = collection(db, 'ninja_preference_requests');
        const q = query(requestsRef, orderBy('requestedAt', 'desc'));

        // Set up real-time listener
        unsubscribe = onSnapshot(q, async (querySnapshot) => {
          try {
            console.log('🔄 Issue #276: Real-time update received - processing preference requests...');

            // Issue #211: Batch fetch all ninja details in parallel to avoid N+1 query problem
            // First, collect all unique ninja IDs
            const ninjaIds = new Set<string>();
            querySnapshot.docs.forEach(requestDoc => {
              const data = requestDoc.data();
              if (data.ninjaId) {
                ninjaIds.add(data.ninjaId);
              }
            });

            // Fetch all ninjas in parallel
            const ninjaMap: { [id: string]: { name: string; email: string } } = {};
            const ninjaFetchPromises = Array.from(ninjaIds).map(async (ninjaId) => {
              try {
                const ninjaRef = doc(db, 'ninjas', ninjaId);
                const ninjaDoc = await getDoc(ninjaRef);
                if (ninjaDoc.exists()) {
                  const ninjaData = ninjaDoc.data();
                  ninjaMap[ninjaId] = {
                    name: ninjaData.fullName || 'Unknown',
                    email: ninjaData.email || '',
                  };
                }
              } catch (error) {
                console.error(`Error fetching ninja ${ninjaId}:`, error);
              }
            });

            await Promise.all(ninjaFetchPromises);
            console.log(`✅ Issue #276: Fetched ${Object.keys(ninjaMap).length} ninja profiles in parallel`);

            // Now build the requests array using the pre-fetched ninja data
            const requestsData: PreferenceRequest[] = querySnapshot.docs.map(requestDoc => {
              const data = requestDoc.data();
              const ninjaInfo = ninjaMap[data.ninjaId] || { name: 'Unknown', email: '' };

              // IMPORTANT: Use currentSegments from the REQUEST record (historical snapshot)
              // NOT from the live ninja profile, to preserve historical accuracy
              const currentSegments = data.currentSegments || [];

              return {
                id: requestDoc.id,
                ninjaId: data.ninjaId,
                ninjaName: ninjaInfo.name,
                ninjaEmail: ninjaInfo.email,
                currentSegments, // From request record, not live ninja profile
                requestedSegments: data.requestedSegments || [],
                status: data.status,
                requestedAt: data.requestedAt?.toDate() || new Date(),
                requestedBy: data.requestedBy,
                adminNotes: data.adminNotes || '',
                processedAt: data.processedAt?.toDate(),
                processedBy: data.processedBy,
              };
            });

            setRequests(requestsData);
            setFilteredRequests(requestsData);
            setLoading(false);

            console.log(`✅ Issue #276: Loaded ${requestsData.length} preference requests in real-time`);
          } catch (error) {
            console.error('❌ Issue #276: Error processing real-time update:', error);
            setLoading(false);
          }
        }, (error) => {
          console.error('❌ Issue #276: Real-time listener error:', error);
          showSnackbar('Failed to load preference requests', 'error');
          setLoading(false);
        });
      } catch (error) {
        console.error('Error initializing preference requests:', error);
        showSnackbar('Failed to load preference requests', 'error');
        setLoading(false);
      }
    };

    initialize();

    // Cleanup function to unsubscribe from real-time listener
    return () => {
      if (unsubscribe) {
        console.log('🧹 Issue #276: Cleaning up real-time listener');
        unsubscribe();
      }
    };
  }, []);

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Filter logic
  const applyFilters = React.useCallback(() => {
    let filtered = [...requests];

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(request => request.status === statusFilter);
    }

    // Segment filter (checks if any of the requested segments match)
    if (segmentFilter !== 'all') {
      filtered = filtered.filter(request => 
        request.requestedSegments.includes(segmentFilter) ||
        request.currentSegments.includes(segmentFilter)
      );
    }

    // Search query (searches in ninja name, email)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(request =>
        request.ninjaName?.toLowerCase().includes(query) ||
        request.ninjaEmail?.toLowerCase().includes(query)
      );
    }

    setFilteredRequests(filtered);
  }, [requests, statusFilter, segmentFilter, searchQuery]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const clearFilters = () => {
    setStatusFilter('all');
    setSegmentFilter('all');
    setSearchQuery('');
  };

  const getAllSegments = () => {
    const segments = new Set<string>();
    requests.forEach(request => {
      request.currentSegments.forEach(seg => segments.add(seg));
      request.requestedSegments.forEach(seg => segments.add(seg));
    });
    return Array.from(segments);
  };

  // Issue #211: Optimized to batch fetch ninja details in parallel
  const loadPreferenceRequests = async () => {
    try {
      setLoading(true);
      const requestsRef = collection(db, 'ninja_preference_requests');
      const q = query(requestsRef, orderBy('requestedAt', 'desc'));

      const querySnapshot = await getDocs(q);

      // Issue #211: Batch fetch all ninja details in parallel to avoid N+1 query problem
      const ninjaIds = new Set<string>();
      querySnapshot.docs.forEach(requestDoc => {
        const data = requestDoc.data();
        if (data.ninjaId) {
          ninjaIds.add(data.ninjaId);
        }
      });

      // Fetch all ninjas in parallel
      const ninjaMap: { [id: string]: { name: string; email: string } } = {};
      const ninjaFetchPromises = Array.from(ninjaIds).map(async (ninjaId) => {
        try {
          const ninjaRef = doc(db, 'ninjas', ninjaId);
          const ninjaDoc = await getDoc(ninjaRef);
          if (ninjaDoc.exists()) {
            const ninjaData = ninjaDoc.data();
            ninjaMap[ninjaId] = {
              name: ninjaData.fullName || 'Unknown',
              email: ninjaData.email || '',
            };
          }
        } catch (error) {
          console.error(`Error fetching ninja ${ninjaId}:`, error);
        }
      });

      await Promise.all(ninjaFetchPromises);

      // Build the requests array using the pre-fetched ninja data
      const requestsData: PreferenceRequest[] = querySnapshot.docs.map(requestDoc => {
        const data = requestDoc.data();
        const ninjaInfo = ninjaMap[data.ninjaId] || { name: 'Unknown', email: '' };

        // IMPORTANT: Use currentSegments from the REQUEST record (historical snapshot)
        // NOT from the live ninja profile, to preserve historical accuracy
        const currentSegments = data.currentSegments || [];

        return {
          id: requestDoc.id,
          ninjaId: data.ninjaId,
          ninjaName: ninjaInfo.name,
          ninjaEmail: ninjaInfo.email,
          currentSegments, // From request record, not live ninja profile
          requestedSegments: data.requestedSegments || [],
          status: data.status,
          requestedAt: data.requestedAt?.toDate() || new Date(),
          requestedBy: data.requestedBy,
          adminNotes: data.adminNotes || '',
          processedAt: data.processedAt?.toDate(),
          processedBy: data.processedBy,
        };
      });

      setRequests(requestsData);
      setFilteredRequests(requestsData);
    } catch (error) {
      console.error('Error loading preference requests:', error);
      showSnackbar('Failed to load preference requests', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Filter logic
  useEffect(() => {
    applyFilters();
  }, [requests, statusFilter, segmentFilter, searchQuery]);

  const handleViewRequest = (request: PreferenceRequest) => {
    setSelectedRequest(request);
    setAdminNotes(request.adminNotes);
    setDialogOpen(true);
  };

  const handleApproveRequest = async () => {
    if (!selectedRequest) return;

    setProcessing(true);
    try {
      // Update the preference request
      const requestRef = doc(db, 'ninja_preference_requests', selectedRequest.id);
      await updateDoc(requestRef, {
        status: 'approved',
        adminNotes,
        processedAt: new Date(),
        processedBy: 'admin',
      });

      // Update the ninja's actual preferences
      const ninjaRef = doc(db, 'ninjas', selectedRequest.ninjaId);
      await updateDoc(ninjaRef, {
        selectedSegments: selectedRequest.requestedSegments,
        updatedAt: new Date(),
        preferenceRequestStatus: 'approved',
        preferenceRequestProcessedAt: Timestamp.now(),
      });

      // Create notification for the ninja
      const segmentNames = selectedRequest.requestedSegments.map(seg =>
        serviceSegmentsMap[seg] || serviceCategoriesMap[seg] || serviceSegmentLabels[seg] || seg
      ).join(', ');

      await addDoc(collection(db, 'notifications'), {
        userId: selectedRequest.ninjaId,
        userType: 'ninja',
        type: 'preference_approved',
        title: 'Service Preferences Approved',
        body: `Your service preference update has been approved. You can now receive jobs for: ${segmentNames}`,
        data: {
          requestId: selectedRequest.id,
          approvedSegments: selectedRequest.requestedSegments,
        },
        read: false,
        createdAt: Timestamp.now(),
      });

      showSnackbar('Preference request approved successfully', 'success');
      setDialogOpen(false);
      loadPreferenceRequests();
    } catch (error) {
      console.error('Error approving request:', error);
      showSnackbar('Failed to approve request', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectRequest = async () => {
    if (!selectedRequest) return;

    setProcessing(true);
    try {
      const requestRef = doc(db, 'ninja_preference_requests', selectedRequest.id);
      await updateDoc(requestRef, {
        status: 'rejected',
        adminNotes,
        processedAt: new Date(),
        processedBy: 'admin',
      });

      // Update ninja document with rejection status
      const ninjaRef = doc(db, 'ninjas', selectedRequest.ninjaId);
      await updateDoc(ninjaRef, {
        preferenceRequestStatus: 'rejected',
        preferenceRequestProcessedAt: Timestamp.now(),
        preferenceRequestReason: adminNotes || 'No reason provided',
      });

      // Create notification for the ninja
      const requestedSegmentNames = selectedRequest.requestedSegments.map(seg =>
        serviceSegmentsMap[seg] || serviceCategoriesMap[seg] || serviceSegmentLabels[seg] || seg
      ).join(', ');

      await addDoc(collection(db, 'notifications'), {
        userId: selectedRequest.ninjaId,
        userType: 'ninja',
        type: 'preference_rejected',
        title: 'Service Preferences Rejected',
        body: adminNotes
          ? `Your service preference update was rejected. Reason: ${adminNotes}`
          : `Your service preference update for ${requestedSegmentNames} was rejected. Please contact support for more information.`,
        data: {
          requestId: selectedRequest.id,
          rejectedSegments: selectedRequest.requestedSegments,
          reason: adminNotes,
        },
        read: false,
        createdAt: Timestamp.now(),
      });

      showSnackbar('Preference request rejected', 'info');
      setDialogOpen(false);
      loadPreferenceRequests();
    } catch (error) {
      console.error('Error rejecting request:', error);
      showSnackbar('Failed to reject request', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const formatSegments = (segments: string[]) => {
    return segments.map(seg => {
      const segmentName = serviceSegmentsMap[seg] || serviceCategoriesMap[seg] || serviceSegmentLabels[seg];
      // If segment is not found in any map, show "Segment Not Available" instead of the ID
      return segmentName || 'Segment Not Available';
    }).join(', ');
  };

  const renderSegmentComparison = (current: string[], requested: string[]) => {
    const added = requested.filter(seg => !current.includes(seg));
    const removed = current.filter(seg => !requested.includes(seg));
    const unchanged = current.filter(seg => requested.includes(seg));

    return (
      <Box>
        {unchanged.length > 0 && (
          <Box mb={1}>
            <Typography variant="body2" color="textSecondary">Unchanged:</Typography>
            {unchanged.map(seg => {
              const segmentName = serviceSegmentsMap[seg] || serviceCategoriesMap[seg] || serviceSegmentLabels[seg];
              return (
                <Chip
                  key={seg}
                  label={segmentName || 'Segment Not Available'}
                  size="small"
                  style={{ margin: 2 }}
                />
              );
            })}
          </Box>
        )}
        {added.length > 0 && (
          <Box mb={1}>
            <Typography variant="body2" color="success.main">Added:</Typography>
            {added.map(seg => {
              const segmentName = serviceSegmentsMap[seg] || serviceCategoriesMap[seg] || serviceSegmentLabels[seg];
              return (
                <Chip
                  key={seg}
                  label={segmentName || 'Segment Not Available'}
                  size="small"
                  color="success"
                  style={{ margin: 2 }}
                />
              );
            })}
          </Box>
        )}
        {removed.length > 0 && (
          <Box mb={1}>
            <Typography variant="body2" color="error.main">Removed:</Typography>
            {removed.map(seg => {
              const segmentName = serviceSegmentsMap[seg] || serviceCategoriesMap[seg] || serviceSegmentLabels[seg];
              return (
                <Chip
                  key={seg}
                  label={segmentName || 'Segment Not Available'}
                  size="small"
                  color="error"
                  style={{ margin: 2 }}
                />
              );
            })}
          </Box>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" disableGutters>
      <PageHeader title="Approvals" subtitle="Ninja requests to change their service preferences" />

      {requests.filter(r => r.status === 'pending').length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have {requests.filter(r => r.status === 'pending').length} pending preference change requests.
        </Alert>
      )}

      {/* Filters Section */}
      <Paper sx={{ p: 2, mb: 2 }}>
        
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search Ninja"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name or email..."
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Service Segment</InputLabel>
              <Select
                value={segmentFilter}
                label="Service Segment"
                onChange={(e) => setSegmentFilter(e.target.value)}
              >
                <MenuItem value="all">All Segments</MenuItem>
                {/* Use only segments from Services & Pricing configuration (service_segments collection) */}
                {Object.entries(serviceSegmentsMap).map(([segmentId, segmentName]) => (
                  <MenuItem key={segmentId} value={segmentId}>
                    {segmentName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={clearFilters}
              fullWidth
            >
              Clear Filters
            </Button>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="body2" color="text.secondary" align="center">
              Showing {filteredRequests.length} of {requests.length} requests
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <ResponsiveTable
        columns={[
          {
            key: 'ninja',
            header: 'Ninja',
            mobile: 'title',
            render: (request: any) => (
              <Box>
                <Typography variant="body1" fontWeight="bold">
                  {request.ninjaName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {request.ninjaEmail}
                </Typography>
              </Box>
            ),
          },
          {
            key: 'segments',
            header: 'Requested Preferences',
            render: (request: any) => (
              <Typography variant="body2">
                {formatSegments(request.requestedSegments)}
              </Typography>
            ),
          },
          {
            key: 'status',
            header: 'Status',
            render: (request: any) => <StatusChip status={request.status} />,
          },
          {
            key: 'requested',
            header: 'Requested Date',
            render: (request: any) => request.requestedAt.toLocaleDateString(),
          },
          {
            key: 'actions',
            header: 'Actions',
            mobile: 'actions',
            render: (request: any) => (
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleViewRequest(request)}
              >
                View Details
              </Button>
            ),
          },
        ]}
        rows={filteredRequests}
        rowKey={(request) => request.id}
        aria-label="Preference approval requests"
        emptyTitle="Nothing awaiting approval"
        emptyDescription="Requests from ninjas to change their service preferences appear here."
      />

      {/* Request Details Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ 
          borderBottom: (t) => `1px solid ${t.palette.divider}`, 
          pb: 2,
          fontSize: '1.25rem',
          fontWeight: 600 
        }}>
          Preference Change Request - {selectedRequest?.ninjaName}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedRequest && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <SectionCard title="Ninja Information" icon={<Person fontSize="small" />} tone="primary">
                    <Stack spacing={2}>
                      <Field label="Name" value={selectedRequest.ninjaName} />
                      <Field label="Email" value={selectedRequest.ninjaEmail} />
                      <Field label="Request Date" value={selectedRequest.requestedAt.toLocaleString()} />
                    </Stack>
                  </SectionCard>
                </Grid>
                <Grid item xs={12} md={6}>
                  <SectionCard title="Status Information" icon={<Info fontSize="small" />} tone="info">
                    <Stack spacing={2}>
                      <Field label="Status" value={<StatusChip status={selectedRequest.status} />} />
                      {selectedRequest.processedAt && (
                        <Field label="Processed" value={selectedRequest.processedAt.toLocaleString()} />
                      )}
                    </Stack>
                  </SectionCard>
                </Grid>
              </Grid>

              <SectionCard title="Preference Changes" icon={<Tune fontSize="small" />} tone="secondary">
                {renderSegmentComparison(selectedRequest.currentSegments, selectedRequest.requestedSegments)}
              </SectionCard>

              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Admin Notes
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add notes about this approval/rejection..."
                  variant="outlined"
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                    }
                  }}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: (t) => `1px solid ${t.palette.divider}`, 
          px: 3, 
          py: 2,
          gap: 1
        }}>
          <Button 
            onClick={() => setDialogOpen(false)}
            sx={{ minWidth: 100 }}
          >
            Cancel
          </Button>
          {selectedRequest?.status === 'pending' && (
            <>
              <Button 
                onClick={handleRejectRequest} 
                color="error"
                variant="outlined"
                disabled={processing}
                sx={{ minWidth: 100 }}
                startIcon={processing ? <CircularProgress size={16} /> : null}
              >
                {processing ? '' : 'Reject'}
              </Button>
              <Button 
                onClick={handleApproveRequest} 
                color="primary" 
                variant="contained"
                disabled={processing}
                sx={{ minWidth: 100 }}
                startIcon={processing ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {processing ? '' : 'Approve'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default NinjaPreferenceApprovals;